import * as Api from '@ViewModels';
import { THEME } from '@Theme';
import { css } from 'aphrodite';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
	Autocomplete,
	AutocompleteContent,
	AutocompleteControl,
	AutocompleteInput,
	AutocompleteItem,
	AutocompleteList,
} from '../../../../web/components-v2/Autocomplete';
import { Button } from '../../../../web/components-v2/Button';
import { isNorthwesternMutualBrokerDealer } from '../../../utils';
import { DateInput } from '../../../../web/components-v2/DateInput';
import { Field, FieldLabel } from '../../../../web/components-v2/Field';
import { Input } from '../../../../web/components-v2/Input';
import { InputGroupAddon, InputGroupInput, InputGroupV2 } from '../../../../web/components-v2/InputGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { useUserAutocompleteQuery } from '../../../../queries/Admin/Account/useUserAutocompleteQuery';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { WebsiteProviderPill } from '../../../components/accountDetails/WebsiteProviderPill';
import { InternalOnly } from '../../../components/InternalOnly';
import { PredictionReasonIndicator } from '../../../components/WillRenewPredictionSelect/PredictionReasonIndicator';
import { WillRenewPredictionSelect } from '../../../components/WillRenewPredictionSelect';
import { AccountNextMeetingField } from './AccountNextMeetingField';
import { AccountAddressField, AccountShareFileFolderField, AccountWebsiteUrlField } from './AccountStructuredFields';
import { AccountStatusChips } from './AccountStatusChips';
import { EmailConfigurationsDialog } from './emailConfig/EmailConfigurationsDialog';
import {
	accountStatusDescription,
	billingCycleDescription,
	creationDateDescription,
	getAccountComponentsLabel,
	getEnabledIntegrationsSummary,
	industryDescription,
	isPaidAccount,
	isPendingCancellation,
	isStillInContract,
	lastCsmMeetingDescription,
	renewalDateLabel,
	renewalOrExpireDateDescription,
	soldByDescription,
	stageDescription,
	successManagerDescription,
	systemOfRecordDescription,
	willRenewPredictionDisplay,
	brokerDealerDescription,
} from '../accountOverviewFormatters';
import { generalInformationCardStyles as gs } from './generalInformationCardStyles';
import { OverviewCard } from './OverviewCard';
import { OverviewField } from './OverviewField';
import { RenewalDateSubLines } from './RenewalDateSubLines';
import { overviewFieldStyles } from './overviewFieldStyles';
import {
	accountCategoryOptions,
	accountCategorySelectionValue,
	brokerDealerSelectionValue,
	brokerDealerValueToApi,
	getBrokerDealerOptions,
	getSystemOfRecordOptions,
	industryOptions,
	industrySelectionValue,
	IOverviewSelectOption,
	stageOptions,
	stageSelectionValue,
	systemOfRecordSelectionValue,
} from './overviewOptions';

/** Accent color for the card's ModuleTag header. */
const CARD_COLOR = THEME.colors.primary.main;

export interface IGeneralInformationCardProps {
	/** The persisted account; drives the read view and seeds the read-mode displays. */
	account: Api.IAccount;
	/** Whether the card is showing its editable form (owned by the parent). */
	isEditMode: boolean;
	/** True only while editing AND the admin holds `ModifyAccount`; gates the field controls. */
	isModifyingAccount: boolean;
	/** The working copy of the account while editing; `undefined` when not editing. */
	draft: Api.IAccount | undefined;
	/** Patch a (possibly dotted) field on the draft. */
	setDraftField: (path: string, value: unknown) => void;
	/** Whether this card is editable at all (e.g. the admin holds `ModifyAccount`). */
	canEdit: boolean;
	/** Whether a save is in flight; disables/spins the Save button. */
	isSaving: boolean;
	/** Enter edit mode. */
	onEdit: () => void;
	/** Commit the current edits. */
	onSave: () => void;
	/** Abandon the current edits. */
	onCancel: () => void;
	/** Company name of the managing account (Northwestern Mutual accounts); the parent fetches it. */
	managementAccountName?: string;
}

/**
 * True only for `http://` / `https://` URLs (case-insensitive, after trim). Used to gate the
 * anchor in `OverviewLink`: a stored `javascript:`/`data:` URL on an externally-sourced field
 * (websiteUrl / shareFileFolderLink) would otherwise be admin-scope stored XSS.
 */
const isSafeHttpUrl = (url: string | undefined): boolean => /^https?:\/\//i.test((url ?? '').trim());

/**
 * "Managed By" read value: a link to the managing account (by company name once the parent has
 * resolved it, otherwise its raw id), or "None" when no management account is set.
 */
const ManagedByValue = ({
	account,
	managementAccountName,
}: {
	account: Api.IAccount;
	managementAccountName?: string;
}) =>
	account.managementAccountId ? (
		<a
			className={css(gs.link)}
			href={`${window.location.origin}/admin.html#/accounts/${account.managementAccountId}`}
			target='_blank'
			rel='noreferrer'
		>
			{managementAccountName || account.managementAccountId}
		</a>
	) : (
		<>None</>
	);

/**
 * Parse an edit-input string into the number the draft field expects: empty/blank → `undefined`
 * (clears the field), unparseable → `undefined` (never store `NaN`), otherwise the numeric value.
 */
const parseNumericInput = (raw: string): number | undefined => {
	const trimmed = raw.trim();
	if (trimmed === '') {
		return undefined;
	}
	const parsed = Number(trimmed);
	return Number.isNaN(parsed) ? undefined : parsed;
};

/**
 * A read-mode primary-color affordance. With an `href` it's a real anchor (View Site, external
 * URLs); without one it's an action — a components-v2 `Button` styled as a link (Add URL / Add
 * Address / Configure Email), not a fake `<a href="#">`.
 */
const OverviewLink = ({
	children,
	href,
	onClick,
}: {
	children: React.ReactNode;
	href?: string;
	onClick?: () => void;
}) =>
	href ? (
		<a className={css(gs.link)} href={href} target='_blank' rel='noopener noreferrer'>
			{children}
		</a>
	) : (
		<Button variant='link' styles={[gs.link]} onClick={onClick}>
			{children}
		</Button>
	);

/**
 * A labeled edit-mode control wrapper. Uses the components-v2 `Field` (vertical orientation stretches
 * its control child to full width, so no per-control width overrides are needed) and reserves the same
 * 48px min-height as read fields to avoid a layout jump when toggling modes.
 */
const EditField = ({ label, children }: { label: string; children: React.ReactNode }) => (
	<Field styles={[gs.editField]}>
		<FieldLabel styles={[gs.editLabel]}>{label}</FieldLabel>
		{children}
	</Field>
);

/** A single-value Select edit control bound to the draft. */
const SelectField = ({
	label,
	value,
	options,
	onChange,
	placeholder,
}: {
	label: string;
	value: string | undefined;
	options: IOverviewSelectOption[];
	onChange: (value: string) => void;
	placeholder?: string;
}) => (
	<EditField label={label}>
		<Select value={value} onValueChange={onChange}>
			<SelectTrigger>
				<SelectValue placeholder={placeholder ?? 'Select One'} />
			</SelectTrigger>
			<SelectContent>
				{options.map(option => (
					<SelectItem key={`${option.value}-${option.label}`} value={option.value}>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	</EditField>
);

/**
 * Debounced people typeahead for Sold By / Success Manager. Owns its own input text + debounce
 * (~250ms) and feeds the debounced fragment to `useUserAutocompleteQuery`. Selecting a user
 * writes the id+name pair (e.g. `salesRepId` + `salesRepName`) back to the draft via the
 * caller-supplied `onSelect`.
 */
const PeopleAutocompleteField = ({
	label,
	currentName,
	onSelect,
}: {
	label: string;
	currentName: string;
	onSelect: (user: Api.IUser) => void;
}) => {
	const [inputValue, setInputValue] = useState(currentName);
	const [debouncedFragment, setDebouncedFragment] = useState('');

	// Re-seed the input when the incoming name changes (cancel-then-reopen, draft re-seed); the
	// initial `useState(currentName)` only runs on mount and would otherwise go stale.
	useEffect(() => {
		setInputValue(currentName);
	}, [currentName]);

	useEffect(() => {
		const handle = setTimeout(() => setDebouncedFragment(inputValue), 250);
		return () => clearTimeout(handle);
	}, [inputValue]);

	const { data } = useUserAutocompleteQuery({ fragment: debouncedFragment, preventImpersonation: true });
	const users = data?.values ?? [];

	const userDisplayName = (user: Api.IUser): string =>
		`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.primaryEmail?.value || user.id;

	return (
		<EditField label={label}>
			<Autocomplete items={users} value={inputValue} onValueChange={setInputValue}>
				<AutocompleteControl>
					<AutocompleteInput placeholder='Search people…' />
				</AutocompleteControl>
				<AutocompleteContent>
					<AutocompleteList>
						{users.map(user => (
							<AutocompleteItem
								key={user.id}
								value={user.id}
								onClick={() => {
									setInputValue(userDisplayName(user));
									onSelect(user);
								}}
							>
								{userDisplayName(user)}
							</AutocompleteItem>
						))}
					</AutocompleteList>
				</AutocompleteContent>
			</Autocomplete>
		</EditField>
	);
};

/**
 * Email Domain & Provider value: one "domain (provider)" line per configuration (or a not-found
 * state), plus a "Configure Email" affordance (gated on `ModifyAccount`) that opens the MobX-free
 * {@link EmailConfigurationsDialog} — restoring the legacy overview's inline email configuration.
 */
const EmailDomainValue = ({ account }: { account: Api.IAccount }) => {
	const canModifyAccount = useAdminUserSession().hasPermission(AdminPermission.ModifyAccount);
	const [configureOpen, setConfigureOpen] = useState(false);
	const configurations = account.emailProviderConfigurations ?? [];
	return (
		<>
			{configurations.length === 0 ? (
				// Legacy fell back to the account's own emailDomain before giving up, so an account with a
				// domain but no provider configuration still shows the domain rather than "Domain Not Found".
				<span className={css(gs.emailProviderName)}>{account.emailDomain || 'Domain Not Found'}</span>
			) : (
				configurations.map((configuration, index) => (
					<div key={`${configuration.domain ?? 'domain'}-${index}`}>
						{configuration.domain ?? 'Domain Not Found'}{' '}
						{configuration.emailProvider && (
							<span className={css(gs.emailProviderName)}>({configuration.emailProvider})</span>
						)}
					</div>
				))
			)}
			{configurations.length === 0 && <div className={css(gs.emailNotConfigured)}>Email not configured!</div>}
			{canModifyAccount && <OverviewLink onClick={() => setConfigureOpen(true)}>Configure Email</OverviewLink>}
			{configureOpen && <EmailConfigurationsDialog account={account} open onClose={() => setConfigureOpen(false)} />}
		</>
	);
};

/** Plan value: plan name plus the website sub-line (with a scheme-validated "View Site" link). */
const PlanValue = ({ account }: { account: Api.IAccount }) => (
	<>
		{account.planDetails?.planName || 'Unknown'}
		{account.features?.websites?.enabled ? (
			<div className={css(gs.planWebsiteLine)}>
				{account.features.websites.tier ? `Website - ${account.features.websites.tier}` : 'Website Customer'}{' '}
				<WebsiteProviderPill websites={account.features.websites} />
				{isSafeHttpUrl(account.features.websites.websiteUrl) && (
					<>
						{' '}
						<OverviewLink href={account.features.websites.websiteUrl}>View Site</OverviewLink>
					</>
				)}
			</div>
		) : null}
	</>
);

/** Status value with the legacy bold-red treatment when the account is CANCELED. */
const StatusValue = ({ account }: { account: Api.IAccount }) => {
	const cancelled = account.activationStatus === Api.ActivationStatus.CANCELED;
	return <span className={cancelled ? css(gs.cancelledStatus) : undefined}>{accountStatusDescription(account)}</span>;
};

/**
 * Read-mode body: one flat field grid (legacy layout) driven by the shared formatters.
 * Internal-only cells (Plan, Billing Cycle, MRR, Components, Status, Creation Date,
 * Renewal Date) are wrapped in `<InternalOnly>`; Account Category is dev-only.
 */
const ReadBody = ({ account, managementAccountName }: { account: Api.IAccount; managementAccountName?: string }) => {
	const isDeveloper = useAdminUserSession().hasPermission(AdminPermission.OnlyForDevelopers);
	const paid = isPaidAccount(account);
	const planMRR = account.planDetails?.planMRR;
	const mrrDisplay = paid ? (planMRR != null ? `$${planMRR}` : '—') : 'N/A';
	const accountCategory = account.preferences?.accountCategory || 'None';
	const pendingCancel = isPendingCancellation(account);
	const renewalProbability = account.additionalInfo?.renewalProbability;
	const renewalProbabilityDisplay =
		renewalProbability != null && renewalProbability >= 0 && renewalProbability <= 100
			? `${renewalProbability}%`
			: 'Unknown';

	return (
		<div className={css(overviewFieldStyles.grid)}>
			<InternalOnly>
				<OverviewField label='Plan'>
					<PlanValue account={account} />
				</OverviewField>
			</InternalOnly>
			<InternalOnly>
				<OverviewField label='Billing Cycle' muted>
					{billingCycleDescription(account)}
				</OverviewField>
			</InternalOnly>
			<InternalOnly>
				<OverviewField label='MRR' muted={mrrDisplay !== `$${planMRR}`}>
					{mrrDisplay}
				</OverviewField>
			</InternalOnly>
			<InternalOnly>
				<OverviewField label='Components' muted>
					{getAccountComponentsLabel(account)}
				</OverviewField>
			</InternalOnly>
			<InternalOnly>
				<OverviewField label='Status'>
					<StatusValue account={account} />
				</OverviewField>
			</InternalOnly>
			<OverviewField label='Sold By' muted>
				{soldByDescription(account)}
			</OverviewField>
			{/* Field order reproduces the legacy grid at four columns, where the pairs Success reads together
				    sat stacked in one column rather than spread across a row: Creation over Renewal Date in the
				    first, Last over Next Meeting in the third. */}
			<OverviewField label='Last Meeting' muted>
				{lastCsmMeetingDescription(account)}
			</OverviewField>
			<OverviewField label='Industry' muted>
				{industryDescription(account)}
			</OverviewField>
			<InternalOnly>
				<OverviewField label='Creation Date'>{creationDateDescription(account)}</OverviewField>
			</InternalOnly>
			<OverviewField label='Success Manager' muted>
				{successManagerDescription(account)}
			</OverviewField>
			<OverviewField label='Next Meeting'>
				<AccountNextMeetingField account={account} />
			</OverviewField>
			<OverviewField label='Broker Dealer' muted>
				{brokerDealerDescription(account)}
			</OverviewField>
			<InternalOnly>
				<OverviewField label={renewalDateLabel(account)} muted tone={pendingCancel ? 'danger' : 'default'}>
					{renewalOrExpireDateDescription(account)}
					<RenewalDateSubLines pendingCancellation={pendingCancel} stillInContract={isStillInContract(account)} />
				</OverviewField>
			</InternalOnly>
			<OverviewField label='Renewal Probability' muted={renewalProbabilityDisplay === 'Unknown'}>
				{renewalProbabilityDisplay}
			</OverviewField>
			<OverviewField label='Stage' muted>
				{stageDescription(account)}
			</OverviewField>
			{/* Legacy parity: read-only, and only present at all once a website stage has been set. */}
			{account.additionalInfo?.websiteStage ? (
				<OverviewField label='Website Stage' muted>
					{account.additionalInfo.websiteStage}
				</OverviewField>
			) : null}
			<OverviewField label='System of Record' muted>
				{systemOfRecordDescription(account)}
			</OverviewField>
			{isNorthwesternMutualBrokerDealer(account) && (
				<InternalOnly>
					<OverviewField label='Managed By' muted={!account.managementAccountId}>
						<ManagedByValue account={account} managementAccountName={managementAccountName} />
					</OverviewField>
				</InternalOnly>
			)}
			<OverviewField label='Email Domain & Provider'>
				<EmailDomainValue account={account} />
			</OverviewField>
			<OverviewField label='Will Renew Prediction' muted>
				<span className={css(gs.willRenewPredictionValue)}>
					{willRenewPredictionDisplay(account.additionalInfo?.willRenewPrediction)}
					<PredictionReasonIndicator reason={account.additionalInfo?.willRenewPredictionReason} />
				</span>
			</OverviewField>
			{isDeveloper && (
				<OverviewField label='Account Category' muted={accountCategory === 'None'}>
					{accountCategory}
				</OverviewField>
			)}
			<OverviewField label='Integrations' muted>
				{getEnabledIntegrationsSummary(account)}
			</OverviewField>
			<OverviewField label='Address'>
				<AccountAddressField account={account} />
			</OverviewField>
			<OverviewField label='Website URL'>
				<AccountWebsiteUrlField account={account} />
			</OverviewField>
			<OverviewField label='ShareFile Folder'>
				<AccountShareFileFolderField account={account} />
			</OverviewField>
		</div>
	);
};

/**
 * Edit-mode body: one flat field grid (legacy layout). Only the fields the legacy overview let
 * admins edit are swapped for components-v2 controls bound to the draft (Sold By, Success Manager,
 * Industry, System of Record, Stage, Next Meeting, Renewal Probability, Will Renew Prediction,
 * Account Category). Everything else — Plan, Billing Cycle, MRR, Components, Status, Last Meeting,
 * Creation Date, Broker Dealer, Renewal Date, Integrations, Address, URLs — renders read-only,
 * matching the read body. Field order and internal-only gating match the read body.
 */
const EditBody = ({
	account,
	draft,
	setDraftField,
}: {
	account: Api.IAccount;
	draft: Api.IAccount;
	setDraftField: (path: string, value: unknown) => void;
}) => {
	const isDeveloper = useAdminUserSession().hasPermission(AdminPermission.OnlyForDevelopers);
	// `WillRenewPredictionSelect` persists on its own and reports failures through the shared
	// error-message view model, so the card supplies it here.
	const errorMessages = useErrorMessages();
	const industry = draft.additionalInfo?.industry;
	const systemOfRecordOptions = useMemo(() => getSystemOfRecordOptions(industry), [industry]);
	// Legacy only offers a Broker Dealer picker for industries that have dealers; null → read-only.
	const brokerDealerOptions = useMemo(() => getBrokerDealerOptions(industry), [industry]);

	const pendingCancel = isPendingCancellation(draft);
	const renewalProbability = draft.additionalInfo?.renewalProbability;
	const planMRR = draft.planDetails?.planMRR;
	const mrrDisplay = isPaidAccount(draft) ? (planMRR != null ? `$${planMRR}` : '—') : 'N/A';

	return (
		<>
			<div className={css(gs.infoStrip)}>
				<span>Editing General Information — update fields and save your changes.</span>
			</div>

			<div className={css(overviewFieldStyles.grid)}>
				<InternalOnly>
					<OverviewField label='Plan'>
						<PlanValue account={draft} />
					</OverviewField>
				</InternalOnly>
				<InternalOnly>
					<OverviewField label='Billing Cycle' muted>
						{billingCycleDescription(draft)}
					</OverviewField>
				</InternalOnly>
				<InternalOnly>
					<OverviewField label='MRR' muted={mrrDisplay !== `$${planMRR}`}>
						{mrrDisplay}
					</OverviewField>
				</InternalOnly>
				<InternalOnly>
					<OverviewField label='Components' muted>
						{getAccountComponentsLabel(draft)}
					</OverviewField>
				</InternalOnly>
				<InternalOnly>
					<OverviewField label='Status'>
						<StatusValue account={draft} />
					</OverviewField>
				</InternalOnly>
				<PeopleAutocompleteField
					label='Sold By'
					currentName={draft.additionalInfo?.salesRepName ?? ''}
					onSelect={user => {
						setDraftField('additionalInfo.salesRepId', user.id);
						setDraftField(
							'additionalInfo.salesRepName',
							`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || (user.primaryEmail?.value ?? '')
						);
					}}
				/>
				{/* Same grid positions as the read view, so toggling Edit doesn't shuffle the grid. */}
				<OverviewField label='Last Meeting' muted>
					{lastCsmMeetingDescription(draft)}
				</OverviewField>
				<SelectField
					label='Industry'
					value={industrySelectionValue(draft.additionalInfo?.industry)}
					options={industryOptions}
					onChange={value => setDraftField('additionalInfo.industry', value)}
				/>
				<InternalOnly>
					<OverviewField label='Creation Date'>{creationDateDescription(draft)}</OverviewField>
				</InternalOnly>
				<PeopleAutocompleteField
					label='Success Manager'
					currentName={draft.additionalInfo?.customerSuccessName ?? ''}
					onSelect={user => {
						setDraftField('additionalInfo.customerSuccessId', user.id);
						setDraftField(
							'additionalInfo.customerSuccessName',
							`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || (user.primaryEmail?.value ?? '')
						);
					}}
				/>
				<EditField label='Next Meeting'>
					<DateInput
						value={
							// Seed in UTC to match the read display (relativeMeetingDescription) + legacy, which
							// treat nextMeeting as a UTC-midnight date; local parsing rolls it back a day.
							draft.additionalInfo?.nextMeeting ? moment.utc(draft.additionalInfo.nextMeeting).format('YYYY-MM-DD') : ''
						}
						onChange={value => setDraftField('additionalInfo.nextMeeting', value)}
					/>
				</EditField>
				{brokerDealerOptions ? (
					<SelectField
						label='Broker Dealer'
						value={brokerDealerSelectionValue(draft.additionalInfo?.brokerDealer)}
						options={brokerDealerOptions}
						placeholder='None'
						onChange={value => setDraftField('additionalInfo.brokerDealer', brokerDealerValueToApi(value))}
					/>
				) : (
					<OverviewField label='Broker Dealer' muted>
						{brokerDealerDescription(draft)}
					</OverviewField>
				)}
				<InternalOnly>
					<OverviewField label={renewalDateLabel(draft)} muted tone={pendingCancel ? 'danger' : 'default'}>
						{renewalOrExpireDateDescription(draft)}
						<RenewalDateSubLines pendingCancellation={pendingCancel} stillInContract={isStillInContract(draft)} />
					</OverviewField>
				</InternalOnly>
				<EditField label='Renewal Probability'>
					<InputGroupV2>
						<InputGroupInput
							value={renewalProbability != null ? String(renewalProbability) : ''}
							placeholder='—'
							onChange={e => {
								const parsed = parseNumericInput(e.target.value);
								// Renewal probability is a percentage — clamp to 0–100 (legacy parity: no >100%).
								const clamped = parsed == null ? undefined : Math.min(100, Math.max(0, parsed));
								setDraftField('additionalInfo.renewalProbability', clamped);
							}}
						/>
						<InputGroupAddon align='inlineEnd'>%</InputGroupAddon>
					</InputGroupV2>
				</EditField>
				<SelectField
					label='Stage'
					value={stageSelectionValue(draft.additionalInfo?.stage)}
					options={stageOptions}
					placeholder='None'
					onChange={value => setDraftField('additionalInfo.stage', value)}
				/>
				{/* Website Stage stays read-only in edit mode too (legacy parity). */}
				{draft.additionalInfo?.websiteStage ? (
					<OverviewField label='Website Stage' muted>
						{draft.additionalInfo.websiteStage}
					</OverviewField>
				) : null}
				<SelectField
					label='System of Record'
					value={systemOfRecordSelectionValue(draft.additionalInfo?.systemOfRecord)}
					options={systemOfRecordOptions}
					placeholder='Select One'
					onChange={value => setDraftField('additionalInfo.systemOfRecord', value)}
				/>
				{/* Gated on the draft, so switching Broker Dealer mid-edit shows/hides the field (legacy parity). */}
				{isNorthwesternMutualBrokerDealer(draft) && (
					<InternalOnly>
						<EditField label='Managed By'>
							<Input
								value={draft.managementAccountId ?? ''}
								placeholder='Management Account ID'
								spellCheck={false}
								onChange={e => setDraftField('managementAccountId', e.target.value)}
							/>
						</EditField>
					</InternalOnly>
				)}
				{/* Email Domain & Provider has no inline control: it is edited through the "Configure Email"
					    dialog that `EmailDomainValue` renders in both read and edit mode. */}
				<OverviewField label='Email Domain & Provider'>
					<EmailDomainValue account={account} />
				</OverviewField>
				{/* Owned by `PATCH additionalInfo/willRenewPrediction` (which also captures the required reason
					    and creates the CSM note server-side), so it saves in place like the structured fields
					    below rather than through the card draft. */}
				<EditField label='Will Renew Prediction'>
					<WillRenewPredictionSelect account={account} errorMessagesVM={errorMessages} renderReasonIndicator />
				</EditField>
				{isDeveloper && (
					<SelectField
						label='Account Category'
						value={accountCategorySelectionValue(draft.preferences?.accountCategory)}
						options={accountCategoryOptions}
						onChange={value => setDraftField('preferences.accountCategory', value)}
					/>
				)}
				<OverviewField label='Integrations' muted>
					{getEnabledIntegrationsSummary(draft)}
				</OverviewField>
				{/* Address / Website URL / ShareFile edit in place via their own field endpoints — independent of
					    the card draft, so they read the persisted account (not the working draft). */}
				<OverviewField label='Address'>
					<AccountAddressField account={account} />
				</OverviewField>
				<OverviewField label='Website URL'>
					<AccountWebsiteUrlField account={account} />
				</OverviewField>
				<OverviewField label='ShareFile Folder'>
					<AccountShareFileFolderField account={account} />
				</OverviewField>
			</div>
		</>
	);
};

/**
 * Account Overview "General Information" card. Presentational + control wiring only — the
 * parent owns the edit lifecycle (begin / draft / save / cancel) and passes it in as props.
 * Renders a read view of the account through the shared formatters, and an edit view that
 * swaps fields for components-v2 controls bound to the parent's draft. The structured fields
 * (Address, Website URL, ShareFile, Will Renew Prediction) edit in place via their own field
 * endpoints — independent of the card draft; Email Domain & Provider is edited through the
 * "Configure Email" dialog.
 */
export const GeneralInformationCard = ({
	account,
	isEditMode,
	isModifyingAccount,
	draft,
	setDraftField,
	canEdit,
	isSaving,
	onEdit,
	onSave,
	onCancel,
	managementAccountName,
}: IGeneralInformationCardProps) => {
	return (
		<OverviewCard
			title='General Information'
			color={CARD_COLOR}
			canEdit={canEdit}
			isEditMode={isEditMode}
			isSaving={isSaving}
			onEdit={onEdit}
			onSave={onSave}
			onCancel={onCancel}
			headerAccessory={
				<InternalOnly>
					<AccountStatusChips account={account} />
				</InternalOnly>
			}
		>
			{isEditMode && isModifyingAccount && draft ? (
				<EditBody account={account} draft={draft} setDraftField={setDraftField} />
			) : (
				<ReadBody account={account} managementAccountName={managementAccountName} />
			)}
		</OverviewCard>
	);
};
